<template>
  <div
    class="t-select"
    :class="[
      {
        't-select--open': open,
        't-select--no-search': !searchable,
      },
    ]"
  >
    <div
      class="t-select__wrapper"
      @mousedown="showDropdown"
      role="combobox"
    >
      <div class="t-select__result">
        <t-tag-list v-if="multiple" inline>
          <t-tag
            v-for="option in selectedValue"
            :key="getOptionKey(option)"
            closeable
            small
            type="plain"
            @close="deselect(option)"
          >
            <slot
              name="selected-option"
              :option="normalizeOption(option)"
            >
              {{ getOptionLabel(option) }}
            </slot>
          </t-tag>
        </t-tag-list>
        <span v-if="!multiple && !isValueEmpty" class="t-select__display">
          <slot
            name="selected-option"
            :option="normalizeOption(selectedValue[0])"
          >
            <span>{{ getOptionLabel(normalizeOption(selectedValue[0])) }}</span>
          </slot>
        </span>
      </div>

      <input
        class="t-select__search"
        v-model="search"
        :placeholder="searchPlaceholder"
        :disabled="disabled"
        :readonly="!searchable"
        :tabindex="tabindex"
        :autocomplete="autocomplete"
        type="search"
        ref="search"
        @compositionstart="composing = true"
        @compositionend="composing = false"
        @keydown="onSearchKeyDown"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
      >

      <div class="t-select__indicator">
        <slot name="indicator">
          <t-icon v-if="!open" :icon="['fas', 'chevron-down']"></t-icon>
          <t-icon v-else :icon="['fas', 'chevron-up']"></t-icon>
        </slot>
      </div>
      <span class="t-select__seperator"></span>
      <ul
        class="t-select__items"
        :class="[
          { 't-select__items--shown': open },
        ]"
        role="listbox"
        @mousedown.prevent="onMouseDown"
        @mouseup="onMouseUp"
      >
        <li
          v-for="(option, index) in filteredOptions"
          class="t-select__item"
          :class="[
            { 't-select__item--selected': isSelected(option) },
            { 't-select__item--highlight': typeAheadPointer === index },
          ]"
          @mousedown.prevent.stop="isSelected(option) ? deselect(option) : select(option)"
          @mouseover="selectable(option) ? typeAheadPointer = index : null"
          :key="getOptionKey(option)"
        >
          <slot name="option" :option="normalizeOption(option)">
            {{ getOptionLabel(option) }}
          </slot>
        </li>

        <li v-if="filteredOptions.length === 0" class="t-select__item t-select__item--no-options">
          <slot name="no-options">{{ noOptionsMessage }}</slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { TIcon } from '../TIcon';
import { TTag, TTagList } from '../TTag';
import ValidatableMixin from '../../mixins/ValidatableMixin';

export default {
  name: 't-select',

  mixins: [ValidatableMixin],

  props: {
    value: {},

    options: {
      type: Array,
      default() {
        return [];
      },
    },

    label: {
      type: String,
      default: 'label',
    },

    valueKey: {
      type: String,
      default: 'id',
    },

    disabled: Boolean,

    searchable: {
      type: Boolean,
      default: true,
    },

    placeholder: String,

    clearSearchOnSelect: {
      type: Boolean,
      default: true,
    },

    closeOnSelect: {
      type: Function,
      default: (multiple) => !multiple,
    },

    noOptionsMessage: {
      type: String,
      default: 'Sorry, no matching options.',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    autocomplete: {
      type: String,
      default: 'off',
    },

    reduce: {
      type: Function,
      default: (option) => option,
    },

    selectable: {
      type: Function,
      default: () => true,
    },

    tabindex: {
      type: Number,
      default: null,
    },

    filterBy: {
      type: Function,
      default(option, label, search) {
        return (label || '').toLowerCase().indexOf(search.toLowerCase()) > -1;
      },
    },

    filter: {
      type: Function,
      default(options, search) {
        return options.filter((option) => {
          let label = this.getOptionLabel(option);
          if (typeof label === 'number') {
            label = label.toString();
          }
          return this.filterBy(option, label, search);
        });
      },
    },

    /**
     * If search text should clear on blur
     * @return {Boolean} True when single and clearSearchOnSelect
     */
    clearSearchOnBlur: {
      type: Function,
      default({ clearSearchOnSelect, multiple }) {
        return clearSearchOnSelect && !multiple;
      },
    },

    selectOnTab: {
      type: Boolean,
      default: true,
    },

    onTab: {
      type: Function,
      default() {
        if (this.selectOnTab && !this.composing) {
          this.typeAheadToggle();
        }
      },
    },

    /**
     * Keycodes that will select the current option.
     * @type Array
     */
    selectOnKeyCodes: {
      type: Array,
      default: () => [13],
    },

    getOptionLabel: {
      type: Function,
      default(option) {
        if (typeof option === 'object') {
          return option[this.label];
        }
        return option;
      },
    },

    getOptionKey: {
      type: Function,
      default(option) {
        if (typeof option === 'object') {
          return option[this.valueKey];
        }
        return option;
      },
    },

    mapKeydown: {
      type: Function,
      default: (map) => map,
    },
  },

  data() {
    return {
      search: '',
      open: false,
      composing: false,
      mousedown: false,
      typeAheadPointer: -1,
      internalValue: [],
    };
  },

  computed: {
    filteredOptions() {
      const optionList = [].concat(this.options);

      if (!this.searchable) {
        return optionList;
      }

      const options = this.search.length ? this.filter(optionList, this.search, this) : optionList;
      return options;
    },

    searchElement() {
      return this.$refs.search;
    },

    isTrackingValues() {
      return typeof this.value === 'undefined' || Object.prototype.hasOwnProperty.call(this.$options.propsData, 'reduce');
    },

    selectedValue() {
      let { value } = this;

      if (this.isTrackingValues) {
        value = this.internalValue;
      }

      if (value) {
        return [].concat(value);
      }

      return [];
    },

    searching() {
      return !!this.search;
    },

    isValueEmpty() {
      return this.selectedValue.length === 0;
    },

    searchPlaceholder() {
      if (this.isValueEmpty && this.placeholder) {
        return this.placeholder;
      }
      return null;
    },
  },

  methods: {
    setInternalValueFromOptions(value) {
      if (Array.isArray(value)) {
        this.internalValue = value.map((val) => this.findOptionFromReducedValue(val));
      } else {
        this.internalValue = this.findOptionFromReducedValue(value);
      }
    },

    select(option) {
      if (!this.isSelected(option)) {
        let value = option;
        if (this.multiple) {
          value = this.selectedValue.concat(option);
        }
        this.updateValue(value);
      }
      this.onAfterSelect();
    },

    deselect(option) {
      this.updateValue(this.selectedValue.filter((val) => !this.optionComparator(val, option)));
    },

    toggle(option) {
      if (this.isSelected(option)) {
        this.deselect(option);
      } else {
        this.select(option);
      }
    },

    clearSelection() {
      this.updateValue(this.multiple ? [] : null);
    },

    clearSearch() {
      this.search = '';
    },

    onAfterSelect() {
      if (this.closeOnSelect(this.multiple)) {
        this.open = !this.open;
        this.searchElement.blur();
      }

      if (this.clearSearchOnSelect) {
        this.clearSearch();
      }
    },

    updateValue(option) {
      if (typeof this.value === 'undefined') {
        this.internalValue = option;
      }

      let value = option;

      if (value !== null) {
        if (Array.isArray(value)) {
          value = value.map((val) => this.reduce(val));
        } else {
          value = this.reduce(value);
        }
      }

      this.$emit('input', value);
      this.$emit('change', value);
    },

    showDropdown(event) {
      const targetIsNotSearch = event.target !== this.searchElement;

      if (targetIsNotSearch) {
        event.preventDefault();
      }

      if (this.open && targetIsNotSearch) {
        this.searchElement.blur();
      } else if (!this.disabled) {
        this.open = true;
        this.searchElement.focus();
      }
    },

    isSelected(option) {
      return this.selectedValue.some((value) => this.optionComparator(value, option));
    },

    optionComparator(a, b) {
      return this.getOptionKey(a) === this.getOptionKey(b);
    },

    findOptionFromReducedValue(value) {
      const predicate = (option) => JSON.stringify(this.reduce(option)) === JSON.stringify(value);

      const matches = this.options.filter(predicate);

      if (matches.length === 1) {
        return matches[0];
      }

      return matches.find((match) => this.optionComparator(match, this.internalValue)) || value;
    },

    closeSearchOptions() {
      this.open = false;
      this.$emit('search:blur');
    },

    maybeDeleteValue() {
      if (this.search.length === 0 && this.selectedValue && this.selectedValue.length) {
        let value = null;

        if (this.multiple) {
          value = [...this.selectedValue.slice(0, this.selectedValue.length - 1)];
        }

        this.updateValue(value);
      }
    },

    normalizeOption(option) {
      return (typeof option === 'object') ? option : { [this.label]: option };
    },

    onEscape() {
      if (!this.search.length) {
        this.searchElement.blur();
      } else {
        this.clearSearch();
      }
    },

    onSearchBlur() {
      if (this.mousedown && !this.searching) {
        this.mousedown = false;
      } else {
        const { clearSearchOnSelect, multiple } = this;

        if (this.clearSearchOnBlur({ clearSearchOnSelect, multiple })) {
          this.clearSearch();
        }
        this.closeSearchOptions();
        return;
      }

      if (this.search.length === 0 && this.options.length === 0) {
        this.closeSearchOptions();
      }
    },

    onSearchFocus() {
      this.open = true;
      this.$emit('search:focus');
    },

    onMouseDown() {
      this.mousedown = true;
    },

    onMouseUp() {
      this.mousedown = false;
    },

    onSearchKeyDown(event) {
      const preventAndSelect = (e) => {
        e.preventDefault();
        return !this.composing && this.typeAheadToggle();
      };

      const defaults = {
        // backspace
        8: () => this.maybeDeleteValue(),

        // tab
        9: () => this.onTab(),

        // escape
        27: () => this.onEscape(),

        // up.prevent
        38: (e) => {
          e.preventDefault();
          return this.typeAheadUp();
        },

        // down.prevent
        40: (e) => {
          e.preventDefault();
          return this.typeAheadDown();
        },
      };

      this.selectOnKeyCodes.forEach((keyCode) => {
        defaults[keyCode] = preventAndSelect;
      });

      const handlers = this.mapKeydown(defaults, this);

      if (typeof handlers[event.keyCode] === 'function') {
        return handlers[event.keyCode](event);
      }
      return null;
    },

    typeAheadUp() {
      // eslint-disable-next-line
      for (let i = this.typeAheadPointer - 1; i >= 0; i--) {
        if (this.selectable(this.filteredOptions[i])) {
          this.typeAheadPointer = i;
          break;
        }
      }
    },

    typeAheadDown() {
      // eslint-disable-next-line
      for (let i = this.typeAheadPointer + 1; i < this.filteredOptions.length; i++) {
        if (this.selectable(this.filteredOptions[i])) {
          this.typeAheadPointer = i;
          break;
        }
      }
    },

    typeAheadToggle() {
      const typeAheadOption = this.filteredOptions[this.typeAheadPointer];

      if (typeAheadOption) {
        this.toggle(typeAheadOption);
      }
    },

    dismiss(event) {
      if (!this.$el.contains(event.target)) {
        this.open = false;
      }
    },
  },

  watch: {
    multiple() {
      this.clearSelection();
    },

    open(isOpen) {
      this.$emit(isOpen ? 'open' : 'close');
    },

    filteredOptions() {
      this.filteredOptions.some((option, index) => {
        if (this.selectable(option)) {
          this.typeAheadPointer = index;
          return true;
        }
        return false;
      });
    },

    options() {
      if (this.value && this.isTrackingValues) {
        this.setInternalValueFromOptions(this.value);
      }
    },

    value(val) {
      if (this.isTrackingValues) {
        this.setInternalValueFromOptions(val);
      }
    },
  },

  created() {
    if (typeof this.value !== 'undefined' && this.isTrackingValues) {
      this.setInternalValueFromOptions(this.value);
    }
  },

  mounted() {
    document.addEventListener('click', this.dismiss);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.dismiss);
  },

  components: {
    TIcon,
    TTag,
    TTagList,
  },
};
</script>
